import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import CookieConsent from './components/common/CookieConsent';
import { ToastProvider, ToastContainer } from './components/common/Toast';
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/auth/ProtectedRoute';
import SessionTimeoutWarning from './components/auth/SessionTimeoutWarning';
import Home from './pages/Home';
import Login from './pages/Login';
import PasswordReset from './pages/PasswordReset';
import AdminPasswordReset from './pages/AdminPasswordReset';
import SurveyRouter from './pages/SurveyRouter';
import Dashboard from './components/admin/Dashboard';
import UserManagement from './pages/UserManagement';
import Analytics from './pages/Analytics';
import StudyList from './pages/studies/StudyList';
import StudyDetail from './pages/studies/StudyDetail';
import CreateStudy from './pages/studies/CreateStudy';
import StudyEnrollment from './pages/studies/StudyEnrollment';
import StudySurvey from './pages/studies/StudySurvey';
import { useAuth } from './hooks/useAuth';
import { initializeSecurity } from './utils/security';
import { initializeSentry } from './utils/sentry';

// Inner component that has access to useAuth
function AppContent() {
  const { showSessionWarning, dismissSessionWarning, logout } = useAuth();

  const handleSessionTimeout = async () => {
    await logout();
    window.location.href = '/login';
  };

  return (
    <>
      <div className="App min-h-screen flex flex-col">
        <Header />

        <main className="flex-grow">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/password-reset" element={<PasswordReset />} />
            <Route path="/admin-password-reset" element={<AdminPasswordReset />} />

            {/* Survey Routes - Public */}
            <Route path="/survey/qr/:qrCode" element={<SurveyRouter />} />
            <Route path="/survey/:surveyId" element={<SurveyRouter />} />

            {/* Study Enrollment Routes - Public */}
            <Route path="/enroll/:studyId" element={<StudyEnrollment />} />
            <Route path="/study/:studyId/survey/:timepointId" element={<StudySurvey />} />

            {/* Protected Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requiredRole={['admin', 'superAdmin']}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute requiredRole={['admin', 'superAdmin']}>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics/:surveyId"
              element={
                <ProtectedRoute requiredRole={['admin', 'superAdmin']}>
                  <Analytics />
                </ProtectedRoute>
              }
            />

            {/* Observational Studies Routes */}
            <Route
              path="/studies"
              element={
                <ProtectedRoute requiredRole={['admin', 'superAdmin']}>
                  <StudyList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/studies/create"
              element={
                <ProtectedRoute requiredRole={['admin', 'superAdmin']}>
                  <CreateStudy />
                </ProtectedRoute>
              }
            />
            <Route
              path="/studies/:studyId"
              element={
                <ProtectedRoute requiredRole={['admin', 'superAdmin']}>
                  <StudyDetail />
                </ProtectedRoute>
              }
            />

            {/* Placeholder Routes for Future Implementation */}
            <Route path="/privacy" element={<PrivacyPlaceholder />} />
            <Route path="/terms" element={<TermsPlaceholder />} />
            <Route path="/contact" element={<ContactPlaceholder />} />
            <Route path="/accessibility" element={<AccessibilityPlaceholder />} />

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        <Footer />

        {/* Cookie Consent Banner */}
        <CookieConsent />

        {/* Toast Notifications */}
        <ToastContainer />
      </div>

      {/* Session Timeout Warning Modal */}
      <SessionTimeoutWarning
        isOpen={showSessionWarning}
        onStayLoggedIn={dismissSessionWarning}
        onLogout={handleSessionTimeout}
      />
    </>
  );
}

function App() {
  // Initialize security measures and error tracking on app startup
  useEffect(() => {
    initializeSecurity();
    initializeSentry();
  }, []);

  return (
    <ErrorBoundary>
      <ToastProvider>
        <Router>
          <AppContent />
        </Router>
      </ToastProvider>
    </ErrorBoundary>
  );
}

// Placeholder components for future implementation
const PrivacyPlaceholder: React.FC = () => (
  <div className="min-h-screen bg-gray-50 py-16">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-white rounded-lg shadow p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
        <p className="text-gray-600 mb-4">
          This page will contain our comprehensive privacy policy detailing how we collect,
          use, and protect patient survey data in compliance with healthcare privacy regulations.
        </p>
        <a href="/" className="text-primary hover:text-primary-dark">← Return to Home</a>
      </div>
    </div>
  </div>
);

const TermsPlaceholder: React.FC = () => (
  <div className="min-h-screen bg-gray-50 py-16">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-white rounded-lg shadow p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Terms of Service</h1>
        <p className="text-gray-600 mb-4">
          This page will contain the terms of service for using the Crisci Create survey platform,
          including user responsibilities and platform usage guidelines.
        </p>
        <a href="/" className="text-primary hover:text-primary-dark">← Return to Home</a>
      </div>
    </div>
  </div>
);

const ContactPlaceholder: React.FC = () => (
  <div className="min-h-screen bg-gray-50 py-16">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-white rounded-lg shadow p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Contact Us</h1>
        <p className="text-gray-600 mb-4">
          This page will contain contact information and a contact form for technical support,
          questions about surveys, or general inquiries.
        </p>
        <a href="/" className="text-primary hover:text-primary-dark">← Return to Home</a>
      </div>
    </div>
  </div>
);

const AccessibilityPlaceholder: React.FC = () => (
  <div className="min-h-screen bg-gray-50 py-16">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-white rounded-lg shadow p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Accessibility Statement</h1>
        <p className="text-gray-600 mb-4">
          This page will contain our accessibility statement and information about how we ensure
          the survey platform is accessible to all users, including those with disabilities.
        </p>
        <a href="/" className="text-primary hover:text-primary-dark">← Return to Home</a>
      </div>
    </div>
  </div>
);

export default App;
